import React, { useState } from "react";
import { Box, Typography, TextField, IconButton, Tooltip, InputBase, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import { BORDER, BRAND, BRAND_SOFT, TEXT_PRIMARY, TEXT_SECONDARY } from "./constants";

// Splits pasted text into separate corrections: one per line, stripping list
// markers such as "1.", "2)", "-", "•" that come from Word / email copies.
const splitCorrections = (text) =>
  text
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*(?:\d+[.)]|[-•*])\s*/, "").trim())
    .filter(Boolean)
    .map((text) => ({ text, source: "manual" }));

const AnnotationSource = ({ item }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mt: 0.25 }}>
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        fontSize: 11.5,
        fontWeight: 600,
        color: "#7c3aed",
        bgcolor: "#f5f3ff",
        px: 0.75,
        py: 0.1,
        borderRadius: 1,
      }}
    >
      <CommentOutlinedIcon sx={{ fontSize: 13 }} />
      הערה מה-PDF{item.sectionLabel ? ` · ${item.sectionLabel}` : ""}
    </Box>
    {item.targetText && (
      <Typography variant="caption" color={TEXT_SECONDARY} noWrap sx={{ maxWidth: 360 }}>
        מסומן: "{item.targetText}"
      </Typography>
    )}
  </Box>
);

const CorrectionsEditor = ({ corrections, onChange, disabled }) => {
  const [draft, setDraft] = useState("");

  const addItems = (items) => {
    if (items.length) onChange([...corrections, ...items]);
  };

  const commitDraft = () => {
    addItems(splitCorrections(draft));
    setDraft("");
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData("text");
    if (/\r?\n/.test(text.trim())) {
      e.preventDefault();
      addItems(splitCorrections(`${draft}${text}`));
      setDraft("");
    }
  };

  const updateItem = (index, text) =>
    onChange(corrections.map((item, i) => (i === index ? { ...item, text } : item)));

  const removeItem = (index) => onChange(corrections.filter((_, i) => i !== index));

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          value={draft}
          disabled={disabled}
          placeholder='לדוגמה: להחליף "קרם לחות" ב-"קרם לחות עשיר" בחזית האריזה'
          onChange={(e) => setDraft(e.target.value)}
          onPaste={handlePaste}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              commitDraft();
            }
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}
        />
        <Button
          variant="outlined"
          onClick={commitDraft}
          disabled={disabled || !draft.trim()}
          startIcon={<AddIcon />}
          sx={{ borderRadius: 2, whiteSpace: "nowrap", height: 40, fontWeight: 600, flexShrink: 0 }}
        >
          הוספה
        </Button>
      </Box>
      <Typography variant="caption" color={TEXT_SECONDARY} sx={{ display: "block", mt: 0.75 }}>
        Enter להוספה · ניתן להדביק רשימה שלמה — כל שורה תהפוך לתיקון נפרד
      </Typography>

      {corrections.length > 0 && (
        <Box
          component="ol"
          sx={{
            listStyle: "none",
            p: 0,
            m: 0,
            mt: 2,
            border: `1px solid ${BORDER}`,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {corrections.map((item, index) => (
            <Box
              component="li"
              key={index}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                px: 1.5,
                py: 1,
                bgcolor: "#fff",
                "&:not(:last-of-type)": { borderBottom: `1px solid ${BORDER}` },
                "&:hover .remove-btn": { opacity: 1 },
              }}
            >
              <Box
                sx={{
                  minWidth: 24,
                  height: 24,
                  mt: "3px",
                  borderRadius: "50%",
                  bgcolor: BRAND_SOFT,
                  color: BRAND,
                  fontSize: 12,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {index + 1}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <InputBase
                  fullWidth
                  multiline
                  value={item.text}
                  disabled={disabled}
                  onChange={(e) => updateItem(index, e.target.value)}
                  onBlur={() => !item.text.trim() && removeItem(index)}
                  inputProps={{ "aria-label": `תיקון ${index + 1}` }}
                  sx={{ fontSize: 14, color: TEXT_PRIMARY, py: 0.25 }}
                />
                {item.source === "annotation" && <AnnotationSource item={item} />}
              </Box>
              <Tooltip title="הסרה">
                <span>
                  <IconButton
                    className="remove-btn"
                    size="small"
                    onClick={() => removeItem(index)}
                    disabled={disabled}
                    aria-label={`הסרת תיקון ${index + 1}`}
                    sx={{ opacity: { xs: 1, md: 0.4 }, transition: "opacity .15s" }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default CorrectionsEditor;
